import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Dumbbell, Clock, Flame, Play, Check, ChevronRight,
  Filter, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { fitnessApi } from '../services/api';
import { toast } from '../hooks/use-toast';

const ProgramsPage = () => {
  const { language } = useLanguage();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const data = await fitnessApi.getPrograms();
      setPrograms(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: 'all', label: language === 'en' ? 'All' : 'Todos' },
    { value: 'goalkeeper', label: language === 'en' ? 'Goalkeeper' : 'Portera' },
    { value: 'strength', label: language === 'en' ? 'Strength' : 'Fuerza' },
    { value: 'cardio', label: 'Cardio' },
    { value: 'flexibility', label: language === 'en' ? 'Flexibility' : 'Flexibilidad' },
    { value: 'football', label: language === 'en' ? 'Football' : 'Fútbol' },
  ];

  const filteredPrograms = programs.filter(p => {
    const matchesFilter = filter === 'all' || p.category === filter;
    const matchesSearch = (language === 'en' ? p.name_en : p.name_es)
      .toLowerCase()
      .includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1f1f] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d1f1f]">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-6 h-6" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-white">
                {language === 'en' ? 'Training Programs' : 'Programas de Entrenamiento'}
              </h1>
              <p className="text-gray-400">
                {language === 'en' ? 'Choose your path to build your wall' : 'Elige tu camino para construir tu muro'}
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              placeholder={language === 'en' ? 'Search programs...' : 'Buscar programas...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-gray-900/50 border-gray-700 text-white h-12"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Button
                key={cat.value}
                variant={filter === cat.value ? 'default' : 'outline'}
                onClick={() => setFilter(cat.value)}
                className={filter === cat.value 
                  ? 'bg-emerald-500 hover:bg-emerald-600 whitespace-nowrap' 
                  : 'border-gray-700 text-gray-400 hover:text-white whitespace-nowrap'
                }
              >
                {cat.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Programs Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPrograms.map((program) => (
            <Link
              key={program.id}
              to={`/programs/${program.id}`}
              className="group bg-gray-900/50 backdrop-blur-sm rounded-3xl overflow-hidden border border-gray-800 hover:border-emerald-500/50 transition-all"
            >
              {/* Image */}
              <div className="aspect-video bg-gradient-to-br from-emerald-500/20 to-teal-500/20 relative overflow-hidden">
                {program.image_url ? (
                  <img 
                    src={program.image_url} 
                    alt={program.name_en}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Dumbbell className="w-16 h-16 text-emerald-500/30" />
                  </div>
                )}
                
                {/* Badge */}
                {program.is_featured && (
                  <div className="absolute top-4 left-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    {language === 'en' ? 'Featured' : 'Destacado'}
                  </div>
                )}
                
                {/* Difficulty */}
                <div className="absolute bottom-4 right-4 bg-black/50 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full">
                  {program.difficulty}
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors">
                  {language === 'en' ? program.name_en : program.name_es}
                </h3>
                <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                  {language === 'en' ? program.description_en : program.description_es}
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {program.duration_weeks} {language === 'en' ? 'weeks' : 'sem'}
                    </span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filteredPrograms.length === 0 && (
          <div className="text-center py-16">
            <Dumbbell className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-500">
              {language === 'en' ? 'No programs found' : 'No se encontraron programas'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramsPage;
